import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { IconButton, Slider, Stack, Tooltip, Typography } from "@mui/material";
import { Fullscreen, FullscreenExit, VolumeOff, VolumeUp } from "@mui/icons-material";

type YoutubeVod = { videoId: string; gameStartSeconds: number };

const YOUTUBE_VIDEO_RE = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/i;
const YOUTUBE_TIME_RE = /[?&#](?:t|start|time_continue)=([0-9hms]+)/i;
const BAR_HEIGHT = 40;

function parseYoutubeTime(raw: string): number {
    if (!raw) return 0;
    if (/^\d+$/.test(raw)) return Number(raw);
    const match = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
    if (!match || match[0] === "") return 0;
    return Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0);
}

export function parseYoutubeVod(url?: string | null): YoutubeVod | null {
    if (!url) return null;
    const videoMatch = url.match(YOUTUBE_VIDEO_RE);
    if (!videoMatch) return null;
    const timeMatch = url.match(YOUTUBE_TIME_RE);
    return {
        videoId: videoMatch[1],
        gameStartSeconds: parseYoutubeTime(timeMatch?.[1] || ""),
    };
}

function formatVideoClock(seconds: number): string {
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function loadYoutubeApi(): Promise<any> {
    const w = window as any;
    if (w.YT?.Player) return Promise.resolve(w.YT);
    return new Promise((resolve) => {
        const previous = w.onYouTubeIframeAPIReady;
        w.onYouTubeIframeAPIReady = () => {
            if (typeof previous === "function") previous();
            resolve(w.YT);
        };
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            const tag = document.createElement("script");
            tag.src = "https://www.youtube.com/iframe_api";
            document.body.appendChild(tag);
        }
        if (w.YT?.Player) resolve(w.YT);
    });
}

export default function GameVodPlayer(props: {
    videoId: string;
    videoTime: number;
    playing: boolean;
    speed: number;
}) {
    const hostRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<any>(null);
    const playingRef = useRef(props.playing);
    const speedRef = useRef(props.speed);
    const videoTimeRef = useRef(Math.max(0, props.videoTime));
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(100);
    const [muted, setMuted] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    playingRef.current = props.playing;
    speedRef.current = props.speed;
    videoTimeRef.current = Math.max(0, props.videoTime);

    useEffect(() => {
        let cancelled = false;
        let player: any;
        const container = hostRef.current;
        if (!container) return;
        container.replaceChildren();
        const mount = document.createElement("div");
        mount.style.width = "100%";
        mount.style.height = "100%";
        container.appendChild(mount);

        loadYoutubeApi().then((YT) => {
            if (cancelled || !mount.isConnected) return;
            player = new YT.Player(mount, {
                videoId: props.videoId,
                width: "100%",
                height: "100%",
                playerVars: {
                    autoplay: 0,
                    controls: 0,
                    disablekb: 1,
                    fs: 0,
                    rel: 0,
                    playsinline: 1,
                    modestbranding: 1,
                    iv_load_policy: 3,
                    origin: window.location.origin,
                    start: Math.floor(videoTimeRef.current),
                },
                events: {
                    onReady: () => {
                        playerRef.current = player;
                        try {
                            player.setPlaybackRate(speedRef.current);
                            player.seekTo(videoTimeRef.current, true);
                            if (typeof player.getVolume === "function") setVolume(player.getVolume());
                            if (typeof player.isMuted === "function") setMuted(Boolean(player.isMuted()));
                            if (typeof player.getDuration === "function") setDuration(player.getDuration() || 0);
                            if (playingRef.current) player.playVideo();
                            else player.pauseVideo();
                        } catch {
                            /* player not ready for commands yet */
                        }
                    },
                },
            });
        });

        return () => {
            cancelled = true;
            playerRef.current = null;
            try {
                player?.destroy();
            } catch {
                /* already gone */
            }
            container.replaceChildren();
        };
    }, [props.videoId]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player || typeof player.getPlayerState !== "function") return;
        const target = videoTimeRef.current;
        try {
            if (typeof player.setPlaybackRate === "function") {
                player.setPlaybackRate(props.speed);
            }
            const current = typeof player.getCurrentTime === "function" ? player.getCurrentTime() : target;
            const drifted = !Number.isFinite(current) || Math.abs(current - target) > 1.25;
            if (!props.playing) {
                if (drifted) player.seekTo(target, true);
                player.pauseVideo();
                return;
            }
            if (drifted) player.seekTo(target, true);
            player.playVideo();
        } catch {
            /* ignore transient API errors */
        }
    }, [props.playing, props.speed, props.videoTime]);

    useEffect(() => {
        const id = window.setInterval(() => {
            const player = playerRef.current;
            if (!player) return;
            try {
                if (typeof player.getCurrentTime === "function") setCurrentTime(player.getCurrentTime() || 0);
                if (typeof player.getDuration === "function") setDuration(player.getDuration() || 0);
            } catch {
                /* ignore */
            }
        }, 250);
        return () => window.clearInterval(id);
    }, [props.videoId]);

    useEffect(() => {
        const onChange = () => setFullscreen(document.fullscreenElement === frameRef.current);
        document.addEventListener("fullscreenchange", onChange);
        return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);

    const toggleMute = () => {
        const player = playerRef.current;
        if (!player) return;
        try {
            if (muted) {
                player.unMute();
                setMuted(false);
            } else {
                player.mute();
                setMuted(true);
            }
        } catch {
            /* ignore */
        }
    };

    const changeVolume = (_: Event, value: number | number[]) => {
        const next = Number(Array.isArray(value) ? value[0] : value);
        const player = playerRef.current;
        setVolume(next);
        if (!player) return;
        try {
            player.setVolume(next);
            if (next > 0 && muted) {
                player.unMute();
                setMuted(false);
            }
        } catch {
            /* ignore */
        }
    };

    const toggleFullscreen = () => {
        const node = frameRef.current;
        if (!node) return;
        if (document.fullscreenElement === node) {
            document.exitFullscreen().catch(() => undefined);
        } else {
            node.requestFullscreen().catch(() => undefined);
        }
    };

    const iconSx = { color: "rgba(255,255,255,0.9)", p: 0.4 };

    return (
        <Box ref={frameRef} sx={{ width: "100%", height: "100%", position: "relative", overflow: "hidden", background: "#000" }}>
            <Box ref={hostRef} sx={{ width: "100%", height: "100%" }} />
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: BAR_HEIGHT,
                    zIndex: 2,
                }}
            />
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: BAR_HEIGHT,
                    px: 1,
                    zIndex: 3,
                    background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.82) 100%)",
                }}
            >
                <Typography sx={{ fontFamily: "monospace", fontSize: "0.72rem", fontWeight: 800, color: "#fff", whiteSpace: "nowrap" }}>
                    {formatVideoClock(currentTime)} / {formatVideoClock(duration)}
                </Typography>
                <Tooltip title={muted ? "Unmute" : "Mute"} arrow>
                    <IconButton size="small" onClick={toggleMute} sx={iconSx}>
                        {muted || volume === 0 ? <VolumeOff sx={{ fontSize: 18 }} /> : <VolumeUp sx={{ fontSize: 18 }} />}
                    </IconButton>
                </Tooltip>
                <Slider
                    size="small"
                    value={muted ? 0 : volume}
                    min={0}
                    max={100}
                    onChange={changeVolume}
                    sx={{
                        width: 88,
                        color: "#fff",
                        "& .MuiSlider-thumb": { width: 10, height: 10 },
                    }}
                />
                <Box sx={{ flex: 1 }} />
                <Tooltip title={fullscreen ? "Exit fullscreen" : "Fullscreen"} arrow>
                    <IconButton size="small" onClick={toggleFullscreen} sx={iconSx}>
                        {fullscreen ? <FullscreenExit sx={{ fontSize: 18 }} /> : <Fullscreen sx={{ fontSize: 18 }} />}
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
}
